/**
 * Backlinks — trigger `[[` urmat de query. Suggestion cu toate notițele
 * tenant-ului (meta, nu conținut). La select inserează NoteMentionNode
 * cu attrs {noteId, title}. Exclude notița curentă din rezultate.
 */
import { Extension, type Editor } from '@tiptap/core';
import { PluginKey } from '@tiptap/pm/state';
import Suggestion, { type SuggestionOptions } from '@tiptap/suggestion';
import { useTranslation } from 'react-i18next';

const BACKLINKS_KEY = new PluginKey('cerbes-backlinks-menu');

import { makeSuggestionRenderer } from '@/features/notes/suggestionRenderer';
import { notesApi, type NoteSummary } from '@/api/notes';

export function createBacklinksMenu(
  tenantId: string,
  currentNoteId?: string | null,
): Extension {
  let cache: NoteSummary[] | null = null;
  let cachedAt = 0;

  const getNotes = async (): Promise<NoteSummary[]> => {
    const now = Date.now();
    if (cache && now - cachedAt < 30_000) return cache;
    if (!tenantId) return [];
    try {
      const list = await notesApi.list(tenantId);
      cache = list;
      cachedAt = now;
      return list;
    } catch {
      return [];
    }
  };

  return Extension.create({
    name: 'backlinksMenu',
    addProseMirrorPlugins() {
      return [
        Suggestion({
          editor: this.editor,
          pluginKey: BACKLINKS_KEY,
          char: '[[',
          startOfLine: false,
          allowSpaces: true,
          items: async ({ query }: { query: string }) => {
            const all = await getNotes();
            const q = query.trim().toLowerCase();
            return all
              .filter((n) => n.id !== currentNoteId)
              .filter(
                (n) =>
                  !q ||
                  n.title.toLowerCase().includes(q) ||
                  n.slug.toLowerCase().includes(q),
              )
              .slice(0, 8);
          },
          command: ({ editor, range, props }: { editor: Editor; range: { from: number; to: number }; props: NoteSummary }) => {
            editor
              .chain()
              .focus()
              .deleteRange(range)
              .insertContent([
                {
                  type: 'noteMention',
                  attrs: { noteId: props.id, title: props.title },
                },
                { type: 'text', text: ' ' },
              ])
              .run();
          },
          render: makeSuggestionRenderer(BacklinkList),
        } as SuggestionOptions),
      ];
    },
  });
}

function BacklinkList(props: {
  items: NoteSummary[];
  command: (item: NoteSummary) => void;
  selectedIndex: number;
}) {
  const { items, command, selectedIndex } = props;
  const { t } = useTranslation();
  if (items.length === 0) {
    return (
      <div className="bg-[var(--bg-elevated)] border border-[var(--border-strong)] rounded-md shadow-lg px-3 py-2 text-xs text-[var(--text-muted)]">
        {t('notes_blocks.backlinks.empty')}
      </div>
    );
  }
  return (
    <div className="bg-[var(--bg-elevated)] border border-[var(--border-strong)] rounded-md shadow-lg py-1 max-h-64 overflow-auto min-w-[260px]">
      {items.map((item, idx) => (
        <button
          key={item.id}
          type="button"
          onClick={() => command(item)}
          className={`w-full text-left px-3 py-1.5 text-xs ${
            idx === selectedIndex
              ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300'
              : 'text-[var(--text)] hover:bg-[var(--bg-hover)]'
          }`}
        >
          <div className="font-medium truncate">{item.title}</div>
          <div className="text-[10px] text-[var(--text-muted)] truncate font-mono">
            {item.slug}
          </div>
        </button>
      ))}
    </div>
  );
}
